import { LinkTo } from '@ember/routing';
import { hash } from '@ember/helper';
import type { TOC } from '@ember/component/template-only';
import { formatDate } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import type {
  IngredientEntry,
  StepEntry,
  SubRecipe,
} from '../../utils/api-types.ts';
import { truncate } from '../../utils/format';
import type { Recipe } from '../../schemas/recipe.ts';
import type { ContentSource } from '../../schemas/content-source.ts';
import type { Creator } from '../../schemas/creator.ts';
import MobileHeader from '../../components/mobile-header.gts';

interface Signature {
  Args: {
    model: {
      data:
        | (Recipe & {
            ingredients: IngredientEntry[];
            steps: StepEntry[];
            subRecipes: SubRecipe[];
          })
        | null;
      source: ContentSource | null;
      creator: Creator | null;
    };
  };
}

const ExploreRecipe: TOC<Signature> = <template>
  <MobileHeader @back={{true}} />

  {{#if @model.data}}
    <div class="mb-4">
      <LinkTo
        @route="explore.index.results"
        @query={{hash type="recipe" page="1"}}
        class="btn btn-ghost btn-sm gap-1"
      >
        &larr; Back to recipes
      </LinkTo>
    </div>

    <div class="bg-base-100 rounded-box p-6 shadow">
      {{#if @model.data.sourceThumbnailUrl}}
        <img
          src={{@model.data.sourceThumbnailUrl}}
          alt={{@model.data.name}}
          class="rounded-box mb-4 max-h-64 w-full object-cover"
        />
      {{/if}}

      <h1 class="mb-2 text-3xl font-bold">
        {{@model.data.name}}
      </h1>
      <div class="mb-4 flex flex-wrap gap-2">
        {{#if @model.data.category}}
          <span class="badge badge-primary">
            {{@model.data.categoryEmoji}}
            {{@model.data.category}}
          </span>
        {{/if}}
        {{#if @model.data.cuisine}}
          <span class="badge badge-accent">
            {{@model.data.cuisine}}
          </span>
        {{/if}}
        {{#if @model.data.difficulty}}
          <span class="badge badge-outline">
            {{@model.data.difficulty}}
          </span>
        {{/if}}
        {{#if @model.data.cookTimeMinutes}}
          <span class="badge badge-ghost">
            {{@model.data.cookTimeMinutes}}
            min
          </span>
        {{/if}}
        {{#if @model.data.servings}}
          <span class="badge badge-ghost">
            {{@model.data.servings}}
            servings
          </span>
        {{/if}}
      </div>

      {{! Source / Creator / Date meta row }}
      {{#if (or @model.source @model.creator)}}
        <div
          class="text-base-content/60 mb-4 flex flex-wrap items-center gap-2 text-sm"
        >
          {{#if @model.creator}}
            <span class="font-medium">
              {{or @model.creator.displayName @model.creator.username}}
            </span>
          {{/if}}
          {{#if @model.source}}
            {{#if @model.creator}}
              <span>&middot;</span>
            {{/if}}
            <span>
              {{truncate (or @model.source.title @model.source.url) 60}}
            </span>
            {{#if @model.source.postedAt}}
              <span>&middot;</span>
              <span>{{formatDate @model.source.postedAt}}</span>
            {{/if}}
          {{/if}}
        </div>
      {{/if}}

      {{#if @model.data.summary}}
        <p class="text-base-content/80 mb-6">
          {{@model.data.summary}}
        </p>
      {{/if}}

      <div class="grid gap-8 md:grid-cols-2">
        {{! Ingredients }}
        {{#if @model.data.ingredients.length}}
          <div>
            <h2 class="mb-3 text-xl font-bold">Ingredients</h2>
            <ul class="list-inside list-disc space-y-1">
              {{#each @model.data.ingredients as |ing|}}
                <li>
                  {{ing.emoji}}
                  <strong>{{ing.measure}}</strong>
                  {{ing.ingredient}}
                  {{#if ing.comment}}
                    —
                    <em>{{ing.comment}}</em>
                  {{/if}}
                </li>
              {{/each}}
            </ul>
          </div>
        {{/if}}

        {{! Steps }}
        {{#if @model.data.steps.length}}
          <div>
            <h2 class="mb-3 text-xl font-bold">Steps</h2>
            <ol class="list-inside list-decimal space-y-2">
              {{#each @model.data.steps as |step|}}
                <li>{{step.instruction}}</li>
              {{/each}}
            </ol>
          </div>
        {{/if}}
      </div>

      {{! Sub-recipes }}
      {{#if @model.data.subRecipes.length}}
        <div class="mt-8">
          <h2 class="mb-4 text-2xl font-bold">Sub-recipes</h2>
          <div class="grid gap-6 md:grid-cols-2">
            {{#each @model.data.subRecipes as |sub|}}
              <div class="bg-base-200 rounded-box p-4">
                <h3 class="mb-2 text-lg font-semibold">{{sub.name}}</h3>
                {{#if sub.summary}}
                  <p
                    class="text-base-content/70 mb-3 text-sm"
                  >{{sub.summary}}</p>
                {{/if}}
                <div class="mb-3 flex flex-wrap gap-1">
                  {{#if sub.category}}
                    <span
                      class="badge badge-sm badge-primary"
                    >{{sub.categoryEmoji}} {{sub.category}}</span>
                  {{/if}}
                  {{#if sub.cuisine}}
                    <span
                      class="badge badge-sm badge-accent"
                    >{{sub.cuisine}}</span>
                  {{/if}}
                  {{#if sub.difficulty}}
                    <span
                      class="badge badge-sm badge-outline"
                    >{{sub.difficulty}}</span>
                  {{/if}}
                  {{#if sub.cookTimeMinutes}}
                    <span
                      class="badge badge-sm badge-ghost"
                    >{{sub.cookTimeMinutes}} min</span>
                  {{/if}}
                </div>
                <div class="grid gap-4 sm:grid-cols-2">
                  {{#if sub.ingredients}}
                    <div>
                      <h4 class="mb-1 text-sm font-medium">Ingredients</h4>
                      <ul class="list-inside list-disc space-y-1 text-sm">
                        {{#each sub.ingredients as |ing|}}
                          <li>
                            {{ing.emoji}}
                            <strong>{{ing.measure}}</strong>
                            {{ing.ingredient}}
                            {{#if ing.comment}}
                              —
                              <em>{{ing.comment}}</em>
                            {{/if}}
                          </li>
                        {{/each}}
                      </ul>
                    </div>
                  {{/if}}
                  {{#if sub.steps}}
                    <div>
                      <h4 class="mb-1 text-sm font-medium">Steps</h4>
                      <ol class="list-inside list-decimal space-y-1 text-sm">
                        {{#each sub.steps as |step|}}
                          <li>{{step.instruction}}</li>
                        {{/each}}
                      </ol>
                    </div>
                  {{/if}}
                </div>
              </div>
            {{/each}}
          </div>
        </div>
      {{/if}}

      {{! Quote }}
      {{#if @model.data.quote}}
        <blockquote class="border-primary mt-6 border-l-4 pl-4 italic">
          "{{@model.data.quote}}"
        </blockquote>
      {{/if}}
    </div>
  {{else}}
    <div class="text-base-content/60 py-12 text-center">
      Recipe not found.
    </div>
  {{/if}}
</template>;

export default ExploreRecipe;
